import React, { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useWidth } from '../../../hooks/useWidth';
import { SessionWithPsychologist } from './data';
import { toast } from 'react-toastify';
import { psychologist } from '../../../types/psychologist';
import { createBlockage } from '../../../hooks/api/blockage';
import CircularProgress from '../../common/CircularProgress';
import { CalendarRecurrentIcon, WarningIcon } from './icons';
import { PanelTimeSelect } from './WorkSchedulePanel';
import { localTzToUTC } from './timeZoneHelpers';
import { formatCalendarDay } from './utils';
import { isImpersonated } from '../../../hooks/api/auth/tokenManager';
import { BA_TIMEZONE } from '../../../utils/timezone';

type BlockMode = 'full-day' | 'time-range';

interface BlockSchedulePanelProps {
  psychologistId: string;
  sessions: SessionWithPsychologist[];
  setSessions: React.Dispatch<React.SetStateAction<SessionWithPsychologist[]>>;
  onClose: () => void;
  onSuccess?: () => void;
  timezone?: string;
  onHasChangesChange?: (hasChanges: boolean) => void;
  /** Called when the user clicks the "Cómo funciona tu agenda" help link. */
  onOpenAgendaHelp?: () => void;
  /** Fecha (YYYY-MM-DD) con la que abre el panel, ej. el feriado desde Comunidad. */
  initialDate?: string;
  /** Motivo con el que abre el panel, ej. el nombre del feriado. */
  initialReason?: string;
}

const BlockSchedulePanel: React.FC<BlockSchedulePanelProps> = ({
  psychologistId,
  sessions,
  setSessions,
  onClose,
  onSuccess,
  timezone = BA_TIMEZONE,
  onHasChangesChange,
  onOpenAgendaHelp,
  initialDate = '',
  initialReason = '',
}) => {
  const { size } = useWidth();
  const isMobile = size === 's';
  const [vpWidth, setVpWidth] = useState(() => window.visualViewport?.width ?? window.innerWidth);
  useEffect(() => {
    if (!isMobile) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => setVpWidth(vv.width);
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => { vv.removeEventListener('resize', update); vv.removeEventListener('scroll', update); };
  }, [isMobile]);
  const [blockMode, setBlockMode] = useState<BlockMode>('full-day');
  const [selectedDate, setSelectedDate] = useState<string>(initialDate);
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [reason, setReason] = useState<string>(initialReason);
  const [loading, setLoading] = useState<boolean>(false);
  const [showSessionWarning, setShowSessionWarning] = useState<boolean>(false);
  const dateInputRef = useRef<HTMLInputElement>(null);

  // Lo precargado (ej. el feriado) no cuenta como cambio: cerrar sin tocar nada no pregunta.
  const hasChanges = selectedDate !== initialDate || !!from || !!to || reason !== initialReason;

  useEffect(() => {
    onHasChangesChange?.(hasChanges);
  }, [hasChanges, onHasChangesChange]);

  // Las psicólogas impersonadas (CX) pueden bloquear días pasados y horarios sin la regla de 24h.
  const impersonated = isImpersonated();

  const minDate = useMemo(() => {
    if (impersonated) return undefined;
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  }, [impersonated]);

  const fromOptions = useMemo(() => {
    if (!selectedDate) return [];

    const options: string[] = [];
    for (let m = 0; m < 24 * 60; m += 30) {
      const h = Math.floor(m / 60);
      const min = m % 60;
      options.push(`${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`);
    }

    const [year, month, day] = selectedDate.split('-').map(Number);
    const dayDate = new Date(year, month - 1, day);
    const in24h = new Date(Date.now() + 24 * 60 * 60 * 1000);

    return options.filter(time => {
      if (impersonated) return true;
      const [h, mi] = time.split(':').map(Number);
      const slotDate = new Date(dayDate);
      slotDate.setHours(h, mi, 0, 0);
      return slotDate >= in24h;
    });
  }, [selectedDate, impersonated]);

  const toOptions = useMemo(() => {
    if (!selectedDate || !from) return [];

    const options: string[] = [];
    for (let m = 30; m <= 24 * 60; m += 30) {
      const h = Math.floor(m / 60);
      const min = m % 60;
      const timeStr = `${String(h === 24 ? 0 : h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
      options.push(timeStr);
    }

    const [year, month, day] = selectedDate.split('-').map(Number);
    const dayDate = new Date(year, month - 1, day);
    const in24h = new Date(Date.now() + 24 * 60 * 60 * 1000);

    return options.filter(time => {
      // 00:00 represents end-of-day (24:00), valid unless from is also 00:00
      if (time === '00:00') return from !== '00:00';
      if (time <= from) return false;
      if (impersonated) return true;
      const [h, mi] = time.split(':').map(Number);
      const slotDate = new Date(dayDate);
      slotDate.setHours(h, mi, 0, 0);
      return slotDate >= in24h;
    });
  }, [selectedDate, from, impersonated]);

  const handleDateChange = (newDate: string) => {
    setSelectedDate(newDate);
    setFrom('');
    setTo('');
  };

  const handleBlockModeChange = (mode: BlockMode) => {
    setBlockMode(mode);
    setFrom('');
    setTo('');
  };

  // Auto-select first option when options change (only in time-range mode)
  React.useEffect(() => {
    if (blockMode === 'time-range' && fromOptions.length > 0 && !from) {
      setFrom(fromOptions[0]);
    }
  }, [fromOptions, from, blockMode]);

  React.useEffect(() => {
    if (blockMode === 'time-range' && toOptions.length > 0 && !to) {
      setTo(toOptions[0]);
    }
  }, [toOptions, to, blockMode]);

  const canConfirm = (() => {
    if (loading || !selectedDate || !reason.trim()) return false;
    if (blockMode === 'full-day') return true;
    return !!(from && to);
  })();

  const createBlockageAndSession = async (
    dateStr: string,
    effectiveFrom: string,
    effectiveTo: string,
    reasonText: string,
  ) => {
    const fromDateUTC = localTzToUTC(dateStr, effectiveFrom, timezone);
    const toDateUTC = localTzToUTC(dateStr, effectiveTo, timezone);

    const utcDate = fromDateUTC.toISOString().split('T')[0];
    const utcStartTime = fromDateUTC.toISOString().split('T')[1].substring(0, 5);
    const utcEndTime = toDateUTC.toISOString().split('T')[1].substring(0, 5);

    await createBlockage({
      date: utcDate,
      start_time: utcStartTime,
      end_time: utcEndTime,
      reason: reasonText,
    });

    const duration = (toDateUTC.getTime() - fromDateUTC.getTime()) / (1000 * 60);
    const blockedSession: SessionWithPsychologist = {
      id: `blocked-manual-${fromDateUTC.toISOString()}`,
      psychologistId,
      date: dateStr,
      time: effectiveFrom,
      modality: 'online',
      status: 'blocked',
      patient: {
        name: reasonText,
        email: 'blocked@slot.com',
      },
      specialty: psychologist.specialties?.[0] || 'general',
      origISO: fromDateUTC.toISOString(),
      duration,
    };
    setSessions(prev => {
      if (!prev) return [blockedSession];
      return [...prev, blockedSession];
    });
  };

  const hasSessionsInRange = useMemo(() => {
    if (!selectedDate) return false;
    const effectiveFrom = blockMode === 'full-day' ? '00:00' : from;
    const effectiveTo = blockMode === 'full-day' ? '23:59' : to;
    if (!effectiveFrom || !effectiveTo) return false;

    const blockStartMin = parseInt(effectiveFrom.split(':')[0], 10) * 60 + parseInt(effectiveFrom.split(':')[1], 10);
    const blockEndMin = parseInt(effectiveTo.split(':')[0], 10) * 60 + parseInt(effectiveTo.split(':')[1], 10);

    return sessions.some(s => {
      if (s.status === 'cancelled' || s.status === 'blocked') return false;
      if (s.date !== selectedDate) return false;
      if (!s.time) return false;
      const [sh, sm] = s.time.split(':').map(Number);
      const sessionStart = sh * 60 + sm;
      const sessionEnd = sessionStart + (s.duration || 60);
      return sessionStart < blockEndMin && sessionEnd > blockStartMin;
    });
  }, [sessions, selectedDate, blockMode, from, to]);

  const handleConfirm = async () => {
    if (!psychologistId || !canConfirm) return;

    if (hasSessionsInRange && !showSessionWarning) {
      setShowSessionWarning(true);
      return;
    }

    try {
      setLoading(true);
      setShowSessionWarning(false);

      const reasonText = reason.trim();

      if (blockMode === 'full-day') {
        await createBlockageAndSession(selectedDate, '00:00', '23:59', reasonText);
      } else {
        await createBlockageAndSession(selectedDate, from, to, reasonText);
      }

      toast.success('Horario ocupado correctamente');

      if (onSuccess) {
        onSuccess();
      }

      // Reset form for next blockage
      setSelectedDate('');
      setFrom('');
      setTo('');
      setReason('');
    } catch (error) {
      toast.error('Ocurrió un error al ocupar el horario. Por favor intentá nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const panelNode = (
    <div id="block-schedule-panel"
      className={
        isMobile
          ? 'fixed bottom-0 left-0 max-h-[85vh] rounded-t-2xl z-[1300] bg-white border border-gray-200 shadow-lg flex flex-col overflow-hidden'
          : 'w-[33%] min-w-[280px] max-w-96 max-h-[37.5rem] bg-white border shadow-lg border-gray-200 rounded-xl flex flex-col overflow-hidden relative z-10'
      }
      style={isMobile ? { width: vpWidth } : undefined}
    >
      {/* Mobile drag handle */}
      {isMobile && (
        <div className="flex justify-center pt-3 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>
      )}
      {/* Header */}
      <div className="py-3.5 px-4 border-b border-gray-200 bg-violet-50 flex items-center justify-between">
        <h3 className="m-0 text-[0.9375rem] font-bold text-violet-700">
          Bloquear espacios
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="flex items-center justify-center w-6 h-6 bg-transparent border-none rounded cursor-pointer text-gray-500 text-base transition-colors hover:bg-gray-100"
          aria-label="Cerrar"
        >
          ×
        </button>
      </div>

      {/* Link de ayuda: "Cómo funciona tu agenda" — común a los 3 modales de configuración */}
      {onOpenAgendaHelp && (
        <div
          data-testid="agenda-help-cta-banner"
          className="shrink-0 px-4 py-2.5 bg-violet-50 border-b border-violet-100 flex items-center"
        >
          <button
            type="button"
            onClick={onOpenAgendaHelp}
            className="text-[0.75rem] text-[#6D57BC] font-medium bg-transparent border-none cursor-pointer p-0 text-left hover:underline underline-offset-2 transition-all"
          >
            ¿Querés entender cómo funciona tu agenda? →
          </button>
        </div>
      )}

      {/* Block mode toggle */}
      <div id="block-mode-tabs" className="flex border-b border-gray-200">
        <button
          type="button"
          onClick={() => handleBlockModeChange('full-day')}
          className={`flex-1 py-2.5 text-xs font-medium border-b-2 transition-colors bg-transparent cursor-pointer ${
            blockMode === 'full-day'
              ? 'text-violet-700 border-violet-700 font-semibold'
              : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          Todo el día
        </button>
        <button
          type="button"
          onClick={() => handleBlockModeChange('time-range')}
          className={`flex-1 py-2.5 text-xs font-medium border-b-2 transition-colors bg-transparent cursor-pointer ${
            blockMode === 'time-range'
              ? 'text-violet-700 border-violet-700 font-semibold'
              : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          Horario específico
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden relative" style={{ scrollbarWidth: 'none' }}>
        {loading && (
          <div className="absolute inset-0 bg-white/80 z-10 grid place-items-center">
            <CircularProgress sizeRem={2} label="Procesando..." />
          </div>
        )}

        <div className="py-3 px-4">
          {/* Date picker */}
          <div id="block-date-picker" className="mb-3">
            <label className="block mb-1.5 text-[0.8125rem] font-semibold text-gray-700">
              Fecha
            </label>
            <div className="relative">
              <input
                type="date"
                ref={dateInputRef}
                value={selectedDate}
                onChange={(e) => handleDateChange(e.target.value)}
                min={minDate}
                className="absolute inset-0 opacity-0 cursor-pointer z-10"
                tabIndex={-1}
                onClick={(e) => { try { (e.currentTarget as HTMLInputElement).showPicker(); } catch { /* noop */ } }}
              />
              <button
                type="button"
                onClick={() => { try { dateInputRef.current?.showPicker(); } catch { /* noop */ } }}
                className="w-full py-2 px-2.5 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 outline-none text-left cursor-pointer hover:border-violet-700 box-border flex items-center justify-between"
              >
                <span className={selectedDate ? 'text-gray-900' : 'text-gray-400'}>
                  {selectedDate ? formatCalendarDay(selectedDate) : 'Seleccionar fecha'}
                </span>
                <CalendarRecurrentIcon size={14} color="#9ca3af" />
              </button>
            </div>
          </div>

          {/* Time selects (only in time-range mode) */}
          {blockMode === 'time-range' && selectedDate && (
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block mb-1.5 text-[0.8125rem] font-semibold text-gray-700">
                  Desde
                </label>
                <PanelTimeSelect
                  value={from}
                  onChange={(v) => { setFrom(v); setTo(''); }}
                  options={fromOptions}
                  ariaLabel="Hora desde"
                />
              </div>

              <div>
                <label className="block mb-1.5 text-[0.8125rem] font-semibold text-gray-700">
                  Hasta
                </label>
                <PanelTimeSelect
                  value={to}
                  onChange={(v) => setTo(v)}
                  options={toOptions}
                  ariaLabel="Hora hasta"
                />
              </div>
            </div>
          )}

          {/* Reason textarea */}
          <div id="block-reason" className="mb-1">
            <label className="block mb-1.5 text-[0.8125rem] font-semibold text-gray-700">
              Razón
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ej: Reunión, espacio administrativo, almuerzo…"
              rows={3}
              className="w-full py-2 px-2.5 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 outline-none resize-vertical font-[inherit] hover:border-violet-700 box-border"
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      {!loading && (
        <div id="block-confirm" className="py-3 px-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            disabled={!canConfirm}
            onClick={handleConfirm}
            style={canConfirm ? { background: '#6D57BC' } : { background: '#9ca3af', opacity: 0.6 }}
            className={`w-full py-2.5 px-4 text-sm font-semibold text-white border-none rounded-lg transition-colors ${
              canConfirm
                ? 'cursor-pointer hover:brightness-90'
                : 'cursor-not-allowed'
            }`}
          >
            Confirmar
          </button>
          {!hasChanges && (
            <button
              type="button"
              onClick={onClose}
              className="w-full mt-1.5 py-2 px-4 text-sm font-semibold text-gray-500 bg-transparent border border-gray-300 rounded-lg cursor-pointer transition-colors hover:bg-gray-100 hover:text-gray-700"
            >
              Cancelar
            </button>
          )}
        </div>
      )}

      {/* Session warning modal */}
      {showSessionWarning && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
        >
          <div style={{ background: '#fff', borderRadius: '1rem', padding: '1.5rem', maxWidth: 380, width: '90%', textAlign: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.75rem' }}>
              <WarningIcon />
            </div>
            <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#374151', marginBottom: '0.75rem' }}>
              Tené en cuenta
            </h3>
            <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '1.5rem', lineHeight: 1.5 }}>
              Las sesiones ya agendadas para este día no se cancelan automáticamente. Deberás reprogramar o cancelar a mano.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <button
                type="button"
                onClick={handleConfirm}
                style={{ background: '#6D57BC', color: '#fff', border: 'none', borderRadius: 9999, padding: '0.625rem 1rem', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer' }}
              >
                Confirmar
              </button>
              <button
                type="button"
                onClick={() => setShowSessionWarning(false)}
                style={{ background: 'transparent', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 9999, padding: '0.625rem 1rem', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer' }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return createPortal(
      <>
        <div className="fixed inset-0 bg-black/40 z-[1299]" onClick={onClose} />
        {panelNode}
      </>,
      document.body,
    );
  }

  return panelNode;
};

export default BlockSchedulePanel;
